var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useMemo, useEffect, useRef, useContext, useCallback, useLayoutEffect, forwardRef, } from "react";
import usePrevious from "@restart/hooks/usePrevious";
import { StyledDropdown, StyledPlaceHolder, StyledSelectButton, StyledClearSelectionButton, StyledAndMoreText, StyledErrorIconContainer, } from "./Select.style";
import useUniqueId from "../../hooks/useUniqueId";
import Box from "../box/Box";
import { StyledErrorIcon } from "../input/Input.style";
import { apolloSpaceInsetHalfX, apolloSpaceInset1X, apolloSpaceInset4X } from "@nexthink/apollo-tokens";
import Menu from "../menu/Menu";
import MenuItem from "../menu/MenuItem";
import MenuGroup from "../menu/MenuGroup";
import Search from "../search/Search";
import Text from "../text/Text";
import Flex from "../box/Flex";
import { ThemeContext } from "../../theme/ThemeProvider";
import isEqual from "lodash/isEqual";
import { trapEvent } from "../../utils/EventHelpers";
import { suggestionLoadingRenderer } from "../tag-editor/TagEditor";
import SelectPreview from "./SelectPreview";
import { useNxTranslation } from "../../i18n/useNxTranslation";
import translationEN from "./locales/translation-en.json";
import translationFR from "./locales/translation-fr.json";
import { getValueFromPixelString } from "../../utils/TokenHelpers";
import FloatingPortalContainer from "../../hoc/FloatingPortalContainer";
import EscapeKeyBoundary from "../../utils/EscapeKeyBoundary";
import { StyledHiddenScreenReaderOnly } from "../../utils/StyleUtils";
import useIdAttrs from "../../hooks/useIdAttrs";
import usePortal, { horizontalAlignment } from "../../hooks/usePortal";
import useForwardedRef from "../../hooks/useForwardedRef";
import { useCalcDropdownItems } from "./useCalcDropdownItems";
const prefixId = "nx-select-";
const clearBtnId = "button-clear-selection";
const MULTISELECT_MAX_VISIBLE_TOOLTIP_LABELS = 5;
const sizeTransformer = (size) => {
    return size === "normal" ? "small" : "medium";
};
export const splitByGroups = (options) => {
    let index = 0;
    const splitGroups = [];
    let lastgroup = undefined;
    let lastgroupIndex = -1;
    const createNewGroup = (groupName, elem) => {
        lastgroupIndex++;
        lastgroup = groupName;
        splitGroups.push([elem]);
    };
    while (index < options.length) {
        if (options[index].group) {
            if (options[index].group === lastgroup) {
                splitGroups[lastgroupIndex].push(options[index]);
            }
            else {
                createNewGroup(options[index].group, options[index]);
            }
        }
        else {
            if (lastgroup || lastgroupIndex === -1) {
                createNewGroup(undefined, options[index]);
            }
            else {
                splitGroups[lastgroupIndex].push(options[index]);
            }
        }
        index++;
    }
    return splitGroups;
};
const getOption = (options, asyncSearch, selectedOption, currentValue, placeholder) => {
    var _a;
    if (asyncSearch) {
        if (currentValue !== undefined) {
            const selectedElement = ((_a = asyncSearch.searchResults) === null || _a === void 0 ? void 0 : _a.find((o) => o.value === currentValue)) ||
                options.find((o) => o.value === currentValue);
            if (!selectedElement) {
                return selectedOption ? selectedOption : { value: currentValue, label: currentValue };
            }
            return selectedElement;
        }
        return undefined;
    }
    if (options.length > 0) {
        if (currentValue !== undefined) {
            return options.find((o) => o.value === currentValue) || options[0];
        }
        if (!placeholder) {
            // return first non disabled option
            const nonDisabledOptions = options.filter((el) => (el.disabled ? false : true));
            return nonDisabledOptions.length > 0 ? nonDisabledOptions[0] : undefined;
        }
    }
    return undefined;
};
const optionsToMenuItems = (options, selectedOptions, insightsAttr, extraTextMaxWidth) => {
    return options === null || options === void 0 ? void 0 : options.map((item, i) => {
        return (React.createElement(MenuItem, { id: item.value, "data-insights": insightsAttr && `${insightsAttr}-option-${item.value}`, "data-label": `${item.label}`, key: `${item.value}-${i}`, selected: selectedOptions === null || selectedOptions === void 0 ? void 0 : selectedOptions.has(item.value), disabled: item.disabled, title: item.label, description: item.description, extraText: item.extraText, extraTextMaxWidth: extraTextMaxWidth },
            React.createElement(SelectPreview, { status: item.preview }, item.label)));
    });
};
// Remove selected elements from initialOptions
const filterInitialOptions = (options, selectedOptions, selectedOption, multiSelect) => {
    if (multiSelect) {
        return options.filter((el) => {
            return !(selectedOptions === null || selectedOptions === void 0 ? void 0 : selectedOptions.some((item) => {
                return item.value === el.value;
            }));
        });
    }
    else {
        return selectedOption
            ? options.filter((el) => {
                return selectedOption.value !== el.value;
            })
            : options;
    }
};
const getOptions = (options, selectedValues, selectedOptions, asyncSearch) => {
    // Handles async search cases
    if (asyncSearch) {
        if (selectedOptions && selectedOptions.length > 0) {
            // filter selectedOptions if not in values
            if (selectedValues) {
                const selectedValuesSet = new Set(selectedValues);
                const optionsStillSelected = selectedOptions.filter((o) => selectedValuesSet.has(o.value));
                const filteredSelectedOptionsSet = new Set(optionsStillSelected.map((item) => item.value));
                const setOfOptions = new Set(options.map((item) => item.value));
                // search both search results and initial options with duplicates
                const allOptions = [
                    ...(asyncSearch.searchResults ? asyncSearch.searchResults.filter((o) => !setOfOptions.has(o.value)) : []),
                    ...options,
                ];
                const selectedInOptions = allOptions.filter((o) => selectedValuesSet.has(o.value) && !filteredSelectedOptionsSet.has(o.value));
                return optionsStillSelected.concat(selectedInOptions);
            }
            else {
                return [];
            }
        }
        else if (selectedValues) {
            if (asyncSearch.searchResults) {
                const setObj = new Set(selectedValues);
                return asyncSearch.searchResults.filter((o) => setObj.has(o.value));
            }
            else {
                return selectedValues.map((el) => {
                    const initialSelectOption = options === null || options === void 0 ? void 0 : options.find((option) => {
                        return option.value === el;
                    });
                    return initialSelectOption ? initialSelectOption : { value: el, label: el };
                });
            }
        }
    }
    else if (options.length > 0 && selectedValues) {
        const setObj = new Set(selectedValues);
        return options.filter((o) => setObj.has(o.value)) || [options[0]];
    }
    return undefined;
};
// eslint-disable-next-line complexity
const Select = forwardRef(function Select(_a, ref) {
    var { id: defaultId, "data-insights": insightsAttr, isDisabled = false, disablePortal = false, name, flip = true, container, placeholder, error, size = "normal", tooltip, options, width: providedWidth, dropdownVisibleItems = "auto", currentValue, currentValues, onChange, onMultiSelectChange, isLoading = false, search, multiSelect = false, dropdownMaxWidth, dropdownSameWidth = true, alignment = "left", asyncSearch, virtualized = false, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, customButton, extraTextMaxWidth, children } = _a, rest = __rest(_a, ["id", "data-insights", "isDisabled", "disablePortal", "name", "flip", "container", "placeholder", "error", "size", "tooltip", "options", "width", "dropdownVisibleItems", "currentValue", "currentValues", "onChange", "onMultiSelectChange", "isLoading", "search", "multiSelect", "dropdownMaxWidth", "dropdownSameWidth", "alignment", "asyncSearch", "virtualized", "aria-label", "aria-labelledby", "customButton", "extraTextMaxWidth", "children"]);
    const id = useUniqueId(prefixId, defaultId);
    const idAttrs = useIdAttrs({ id, insightsAttr });
    const errorIdAttrs = idAttrs("-error");
    const selectBtnIdAttrs = idAttrs("-button");
    const searchIdAttrs = idAttrs("-search");
    const menuId = `${id}-menu`;
    const width = providedWidth || (children ? "fit-content" : "264px");
    const { t, nxI18n } = useNxTranslation();
    nxI18n.addResources("en", "select", translationEN);
    nxI18n.addResources("fr", "select", translationFR);
    const theme = useContext(ThemeContext);
    // should show results if they are loaded
    const shouldShowOptions = useMemo(() => {
        return !(asyncSearch && !asyncSearch.searchResults);
    }, [asyncSearch, asyncSearch === null || asyncSearch === void 0 ? void 0 : asyncSearch.searchResults]);
    const [visible, setVisible] = useState(false);
    const [forcedWidth, setForcedWidth] = useState(undefined);
    const [selectedOption, setSelectedOption] = useState(!multiSelect ? getOption(options, asyncSearch, undefined, currentValue, placeholder) : undefined);
    const [selectedOptions, setSelectedOptions] = useState(multiSelect ? getOptions(options, currentValues, undefined, asyncSearch) : undefined);
    const [filteredOptions, setFilteredOptions] = useState();
    const [menuSelectedOptions, setMenuSelectedOptions] = useState([]);
    const [menuInitialOptions, setMenuInitialOptions] = useState([]);
    const hasSearch = useMemo(() => {
        return asyncSearch ? true : search;
    }, [search, asyncSearch]);
    const splitedOptions = useMemo(() => splitByGroups(filteredOptions || options), [options, filteredOptions]);
    /**
     *  Refs
     */
    const [btnRef, setBtnRef] = useForwardedRef(ref);
    const [dropdownRef, setDropdownRef] = useState(null);
    const initialRender = useRef(true);
    const searchRef = useRef(null);
    const previousOptions = usePrevious(options);
    const { getPortalProps, getTargetProps, portalStyles, update } = usePortal({
        onVisibleChange: (open) => {
            if (!open) {
                setFilteredOptions(undefined);
                asyncSearch === null || asyncSearch === void 0 ? void 0 : asyncSearch.onSearch("");
            }
            setVisible(open);
        },
        visible,
        trigger: "click",
        portalOptions: {
            flip,
            sameWidthTarget: dropdownSameWidth,
            shouldHideWhenTargetHidden: false,
        },
        placement: horizontalAlignment[alignment],
        portalRef: dropdownRef,
        targetRef: btnRef.current,
    });
    const { visibleItems, asyncSearchVisibleItems } = useCalcDropdownItems({
        dropdownVisibleItems,
        floatingUpdate: update,
        dropdownRef,
        visible,
        hasSearch,
        multiSelect,
        asyncSearch,
        size: sizeTransformer(size),
        options,
        filteredOptions,
        selectedOptions: menuSelectedOptions,
        selectedOption,
        unSelectedInitialOptions: menuInitialOptions,
    });
    const closeSelect = () => {
        setVisible(false);
        setFilteredOptions(undefined);
        asyncSearch === null || asyncSearch === void 0 ? void 0 : asyncSearch.onSearch("");
    };
    useEffect(() => {
        if (multiSelect) {
            return;
        }
        if (initialRender.current) {
            initialRender.current = false;
            if (!currentValue && !placeholder) {
                onChange === null || onChange === void 0 ? void 0 : onChange(selectedOption);
            }
        }
        else if (!asyncSearch && !isEqual(options, previousOptions)) {
            closeSelect();
            // check if value still exist
            const currentValueExist = options.find((o) => o.value === currentValue);
            if (!currentValueExist) {
                onChange === null || onChange === void 0 ? void 0 : onChange(!placeholder && options.length > 0 ? options[0] : undefined);
            }
        }
        update();
    }, [options, multiSelect]);
    useEffect(() => {
        if (asyncSearch) {
            setFilteredOptions(asyncSearch.searchResults);
        }
    }, [asyncSearch, asyncSearch === null || asyncSearch === void 0 ? void 0 : asyncSearch.searchResults]);
    useEffect(() => {
        if (multiSelect) {
            setSelectedOptions(getOptions(options, currentValues, selectedOptions, asyncSearch));
        }
        else {
            const newSelectedOption = getOption(options, asyncSearch, selectedOption, currentValue, placeholder);
            setSelectedOption(newSelectedOption);
        }
    }, [currentValues, currentValue, asyncSearch === null || asyncSearch === void 0 ? void 0 : asyncSearch.searchResults, options]);
    useEffect(() => {
        arrangeMenuOptions();
    }, [visible, filteredOptions]);
    useLayoutEffect(() => {
        if (!virtualized || dropdownSameWidth || !visible) {
            setForcedWidth(undefined);
        }
        else {
            const menu = document.getElementById(menuId);
            if (menu) {
                const scrollBarWidth = 12;
                const descriptionPlaceholderWidth = getValueFromPixelString(apolloSpaceInset4X);
                const paddings = getValueFromPixelString(apolloSpaceInsetHalfX) * 2 + descriptionPlaceholderWidth;
                // because of absolute positioning of virtualized list items we have to measure the size
                // of the list item's element with content otherwise
                const listItemContentSelector = ":scope li span";
                const widestListItem = Array.from(menu.querySelectorAll(listItemContentSelector)).reduce((prev, cur) => (cur.clientWidth > prev ? cur.clientWidth : prev), 0);
                // Add some space if there is a scrollbar
                widestListItem && setForcedWidth(widestListItem + paddings + scrollBarWidth);
            }
        }
    });
    const arrangeMenuOptions = () => {
        // Distribute Options in the selected part or initial options part of the menu
        let unSelectedInitial = [];
        let filteredSelectedOptions = [];
        if (multiSelect) {
            unSelectedInitial = filterInitialOptions(options, selectedOptions, undefined, true);
            filteredSelectedOptions =
                (selectedOptions === null || selectedOptions === void 0 ? void 0 : selectedOptions.filter((opt) => {
                    return !(unSelectedInitial === null || unSelectedInitial === void 0 ? void 0 : unSelectedInitial.some((item) => {
                        return item.value === opt.value;
                    }));
                })) || [];
        }
        else {
            unSelectedInitial = filterInitialOptions(options, undefined, selectedOption, false);
            if (selectedOption) {
                filteredSelectedOptions = [selectedOption];
            }
        }
        setMenuSelectedOptions(filteredSelectedOptions);
        setMenuInitialOptions(unSelectedInitial);
    };
    const handleOnSelect = (optionValue) => {
        var _a, _b;
        if (!selectedOption || selectedOption.value !== optionValue) {
            let elems;
            if (asyncSearch) {
                if (asyncSearch.searchResults && asyncSearch.searchResults.length > 0) {
                    elems = (_a = asyncSearch.searchResults) === null || _a === void 0 ? void 0 : _a.find((o) => o.value === optionValue);
                }
                else {
                    elems = options.find((o) => o.value === optionValue);
                }
            }
            else {
                elems = options.find((o) => o.value === optionValue);
            }
            if (elems) {
                onChange === null || onChange === void 0 ? void 0 : onChange(elems);
            }
        }
        closeSelect();
        update();
        (_b = btnRef.current) === null || _b === void 0 ? void 0 : _b.focus();
    };
    const handleOnMultiSelect = (optionValues, optionId) => {
        let setObj;
        if (asyncSearch) {
            const selection = selectedOptions || [];
            const selectedOptionsSet = new Set([...selection.map((o) => o.value)]);
            if (selectedOptionsSet.has(optionId)) {
                // deselect
                const newSelectedOptions = selectedOptions === null || selectedOptions === void 0 ? void 0 : selectedOptions.filter((o) => o.value !== optionId);
                // Remove Async Search Options from the Menu Selected Element Section
                if (!options.find((o) => o.value === optionId) && menuSelectedOptions.find((o) => o.value === optionId)) {
                    setMenuSelectedOptions(menuSelectedOptions === null || menuSelectedOptions === void 0 ? void 0 : menuSelectedOptions.filter((o) => o.value !== optionId));
                }
                onMultiSelectChange === null || onMultiSelectChange === void 0 ? void 0 : onMultiSelectChange(newSelectedOptions);
            }
            else {
                // select an element
                const allOptionsList = [...((asyncSearch === null || asyncSearch === void 0 ? void 0 : asyncSearch.searchResults) ? asyncSearch.searchResults : []), ...options];
                const newElem = allOptionsList.find((o) => o.value === optionId) || menuSelectedOptions.find((o) => o.value === optionId);
                const newSelection = newElem ? [...selection, newElem] : [...selection];
                onMultiSelectChange === null || onMultiSelectChange === void 0 ? void 0 : onMultiSelectChange(newSelection);
            }
        }
        else {
            if (hasSearch && selectedOptions && optionValues.includes(optionId)) {
                // an item is selected when search is used
                setObj = new Set([...selectedOptions.map((o) => o.value), ...optionValues]);
            }
            else if (hasSearch && selectedOptions && !optionValues.includes(optionId)) {
                // an item is deselected when search is used
                setObj = new Set(selectedOptions === null || selectedOptions === void 0 ? void 0 : selectedOptions.filter((o) => o.value !== optionId).map((o) => o.value));
            }
            else {
                setObj = new Set(optionValues);
            }
            onMultiSelectChange === null || onMultiSelectChange === void 0 ? void 0 : onMultiSelectChange(options.filter((o) => setObj.has(o.value)));
        }
        update();
    };
    const handleSearch = (value) => {
        var _a;
        if (asyncSearch) {
            asyncSearch.onSearch(value);
        }
        else {
            setFilteredOptions(options.filter((option) => {
                if (option.group) {
                    return (option.group.toLowerCase().includes(value.toLowerCase()) ||
                        option.label.toLowerCase().includes(value.toLowerCase()));
                }
                return option.label.toLowerCase().includes(value.toLowerCase());
            }));
        }
        (_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const renderError = (errorMessage) => (React.createElement(StyledErrorIconContainer, Object.assign({}, errorIdAttrs, (errorMessage && { "aria-live": "polite" })),
        React.createElement(Box, { as: "span", display: "inline-block", width: "100%", height: "100%", title: errorMessage, "aria-hidden": "true" },
            React.createElement(StyledErrorIcon, null)),
        React.createElement(StyledHiddenScreenReaderOnly, null, errorMessage || "Invalid data")));
    const isItemSelected = useCallback((item) => {
        if (multiSelect) {
            const setObj = new Set(currentValues);
            return setObj.has(item.value);
        }
        else {
            return item.value === currentValue;
        }
    }, [currentValue, currentValues]);
    const flattenGroupForJSX = useCallback(() => {
        const elems = [];
        splitedOptions.forEach((group) => {
            if (group[0].group) {
                elems.push(React.createElement(MenuGroup, { id: group[0].group, key: group[0].group, name: group[0].group }, group.map((item, i) => {
                    return (React.createElement(MenuItem, { id: `${item.value}`, "data-insights": insightsAttr && `${insightsAttr}-option-${item.value}`, "data-label": item.group ? `${item.group}-${item.label}` : `${item.label}`, key: `${item.value}-${i}`, selected: isItemSelected(item), disabled: item.disabled, title: item.label, description: item.description, extraText: item.extraText, extraTextMaxWidth: extraTextMaxWidth },
                        React.createElement(SelectPreview, { status: item.preview }, item.label)));
                })));
            }
            else {
                group.forEach((item, i) => {
                    elems.push(React.createElement(MenuItem, { id: `${item.value}`, "data-insights": insightsAttr && `${insightsAttr}-option-${item.value}`, "data-label": item.group ? `${item.group}-${item.label}` : `${item.label}`, key: `${item.value}-${i}`, selected: isItemSelected(item), disabled: item.disabled, title: item.label, description: item.description, extraText: item.extraText, extraTextMaxWidth: extraTextMaxWidth },
                        React.createElement(SelectPreview, { status: item.preview }, item.label)));
                });
            }
        });
        return elems;
    }, [splitedOptions, currentValue, currentValues]);
    const renderAsyncSearchSelectionInMenu = () => {
        if (multiSelect) {
            const selectedOptionsAsSet = new Set(currentValues);
            const selectedMenuItems = optionsToMenuItems(menuSelectedOptions, selectedOptionsAsSet, insightsAttr, extraTextMaxWidth);
            if (options.length > 0) {
                const initialOptionMenuItems = optionsToMenuItems(menuInitialOptions, selectedOptionsAsSet, insightsAttr, extraTextMaxWidth);
                return selectedMenuItems ? [...selectedMenuItems, ...initialOptionMenuItems] : initialOptionMenuItems;
            }
            return selectedMenuItems;
        }
        else {
            const selectedMenuItems = currentValue
                ? [
                    React.createElement(MenuItem, { id: "deselect-any", "data-insights": insightsAttr && `${insightsAttr}-option-any`, key: "deselect-any", onClick: () => {
                            onChange === null || onChange === void 0 ? void 0 : onChange(undefined);
                        } }, t("any", { ns: "select" })),
                    React.createElement(MenuItem, { id: `selected-${currentValue}`, "data-insights": insightsAttr && `${insightsAttr}-option-${currentValue}`, key: `selected-${currentValue}`, disabled: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.disabled, selected: true, description: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.description, extraText: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.extraText, extraTextMaxWidth: extraTextMaxWidth }, selectedOption ? selectedOption.label : currentValue),
                ]
                : [
                    React.createElement(MenuItem, { id: "deselect-any", "data-insights": insightsAttr && `${insightsAttr}-option-any`, key: "deselect-any", selected: true }, t("any", { ns: "select" })),
                ];
            // Add initial values under the selected elements
            if (options.length > 0) {
                const initialOptionMenuItems = optionsToMenuItems(menuInitialOptions, undefined, insightsAttr, extraTextMaxWidth);
                return [...selectedMenuItems, ...initialOptionMenuItems];
            }
            return selectedMenuItems;
        }
    };
    const onMenuKeyDown = (event) => {
        var _a, _b, _c, _d;
        if (event.key === "Tab") {
            if (event.shiftKey) {
                if (hasSearch) {
                    (_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    trapEvent(event);
                }
                else {
                    closeSelect();
                }
            }
            else {
                if (((_c = (_b = document.getElementById(clearBtnId)) === null || _b === void 0 ? void 0 : _b.tabIndex) !== null && _c !== void 0 ? _c : -1) >= 0) {
                    (_d = document.getElementById(clearBtnId)) === null || _d === void 0 ? void 0 : _d.focus();
                    trapEvent(event);
                }
                else {
                    closeSelect();
                }
            }
        }
    };
    const renderMenu = () => {
        return (React.createElement(Menu, Object.assign({ id: menuId, "data-insights": insightsAttr && `${insightsAttr}-options`, "aria-label": "select menu", onMultiSelect: handleOnMultiSelect, onSelect: handleOnSelect, size: sizeTransformer(size), isContentWidth: !dropdownSameWidth, variant: "select", multiSelect: multiSelect, menuVisibleItems: asyncSearch && !shouldShowOptions ? asyncSearchVisibleItems : visibleItems, virtualized: virtualized, onKeyDown: onMenuKeyDown, allowTab: false, 
            // trap tab ourselves to allow us to choose when to allow it to propagate to move focus
            trapTab: false }, (hasSearch && { focusedIndex: -1 })), asyncSearch && !asyncSearch.searchResults ? renderAsyncSearchSelectionInMenu() : flattenGroupForJSX()));
    };
    const handleClearBtnOnKeyDown = (event) => {
        var _a;
        if (event.key === "Tab") {
            if (event.shiftKey) {
                const firstOption = firstSelectOptionToFocus();
                if (firstOption) {
                    firstOption === null || firstOption === void 0 ? void 0 : firstOption.focus();
                }
                else {
                    if (hasSearch) {
                        (_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    }
                }
                trapEvent(event);
            }
            else {
                closeSelect();
            }
        }
    };
    const renderClearSelection = () => {
        return (React.createElement(Box, { p: apolloSpaceInset1X, pt: apolloSpaceInsetHalfX, pb: apolloSpaceInsetHalfX, bg: theme.colors.apolloColorBackgroundMenuItemDefault, onKeyDown: handleClearBtnOnKeyDown },
            React.createElement(StyledClearSelectionButton, { id: clearBtnId, size: "small", variant: "tertiary", "aria-label": "clear selection", disabled: !currentValues || currentValues.length === 0, onClick: () => {
                    var _a, _b;
                    if (asyncSearch && options.length > 0) {
                        // make sure the deselected element if from initialOptions is back in the unselected list
                        setMenuSelectedOptions([]);
                        setMenuInitialOptions(options);
                    }
                    onMultiSelectChange === null || onMultiSelectChange === void 0 ? void 0 : onMultiSelectChange([]);
                    update();
                    // Focus after a clearAll
                    if (hasSearch) {
                        (_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    }
                    else {
                        const menu = document.getElementById(menuId);
                        const menuOptions = menu === null || menu === void 0 ? void 0 : menu.querySelectorAll('[role="option"]');
                        (_b = menuOptions === null || menuOptions === void 0 ? void 0 : menuOptions[0]) === null || _b === void 0 ? void 0 : _b.focus();
                    }
                } }, t("deselectAll", { ns: "select" }))));
    };
    const renderAndMore = () => {
        return (React.createElement(Box, { p: apolloSpaceInset1X, bg: theme.colors.apolloColorBackgroundMenuItemDefault },
            React.createElement(StyledAndMoreText, { id: `${id}-andmore`, variant: "verySmall" }, t("more", { ns: "select" }))));
    };
    const firstSelectOptionToFocus = () => {
        // return first option to focus on in select
        const menuElement = document.getElementById(menuId);
        const firstSelectedItem = menuElement.querySelector("li[aria-checked='true']:not([aria-disabled])");
        const firstOption = menuElement.querySelector("li[role='option']:not([aria-disabled])");
        return firstSelectedItem || firstOption;
    };
    const handleSearchOnKeyDown = (event) => {
        if (event.key === "Tab") {
            if (event.shiftKey) {
                closeSelect();
            }
            else {
                const firstOption = firstSelectOptionToFocus();
                if (firstOption) {
                    firstOption === null || firstOption === void 0 ? void 0 : firstOption.focus();
                }
                trapEvent(event);
            }
        }
    };
    const renderSearch = () => {
        return (React.createElement(React.Fragment, null,
            React.createElement(Box, Object.assign({ bg: theme.colors.apolloColorBackgroundMenuItemDefault, p: apolloSpaceInset1X }, (size === "normal" && { pb: apolloSpaceInsetHalfX }), { onKeyDown: handleSearchOnKeyDown }),
                React.createElement(Search, Object.assign({ ref: searchRef, autoFocus: true }, searchIdAttrs, { onSearch: handleSearch, placeholder: t("search", { ns: "select" }) }))),
            (filteredOptions === null || filteredOptions === void 0 ? void 0 : filteredOptions.length) === 0 && (React.createElement(Flex, { alignItems: "center", px: apolloSpaceInset1X, py: size === "large" ? apolloSpaceInset1X : apolloSpaceInsetHalfX, bg: theme.colors.apolloColorBackgroundMenuItemDefault },
                React.createElement(Text, { variant: "bodySmall", color: theme.colors.apolloColorTextSecondary },
                    React.createElement(Text, null, t("noResults", { ns: "select" })))))));
    };
    const dropdown = (React.createElement(EscapeKeyBoundary, null,
        React.createElement(StyledDropdown, Object.assign({ id: `${id}-dropdown`, ref: setDropdownRef, "$width": width, dropdownMaxWidth: dropdownMaxWidth, dropdownSameWidth: dropdownSameWidth, forcedWidth: forcedWidth, style: portalStyles }, getPortalProps({
            onKeyDown: (e) => {
                var _a;
                if (e.key === "Tab") {
                    closeSelect();
                    (_a = btnRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                }
            },
        })), visible && !isDisabled && (React.createElement(React.Fragment, null,
            hasSearch && renderSearch(),
            renderMenu(),
            !isLoading && !shouldShowOptions && options.length > 0 && !(asyncSearch === null || asyncSearch === void 0 ? void 0 : asyncSearch.hideAndMore) && renderAndMore(),
            isLoading && suggestionLoadingRenderer(),
            multiSelect && renderClearSelection())))));
    useLayoutEffect(() => {
        var _a;
        (_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [isLoading]);
    const renderSelectButtonText = () => {
        var _a;
        const shouldShowPlaceHolder = (!multiSelect && currentValue === undefined && (placeholder || asyncSearch)) ||
            (multiSelect && (currentValues === undefined || currentValues.length === 0));
        if (shouldShowPlaceHolder) {
            return placeholder ? React.createElement(StyledPlaceHolder, null, placeholder) : React.createElement(React.Fragment, null, t("any", { ns: "select" }));
        }
        else if (multiSelect && currentValues) {
            return (currentValues === null || currentValues === void 0 ? void 0 : currentValues.length) > 1 ? (React.createElement(StyledPlaceHolder, null, `${currentValues === null || currentValues === void 0 ? void 0 : currentValues.length} ${t("selected", { ns: "select" })}`)) : (React.createElement(React.Fragment, null, (_a = selectedOptions === null || selectedOptions === void 0 ? void 0 : selectedOptions[0]) === null || _a === void 0 ? void 0 : _a.label));
        }
        else if (currentValue !== undefined && selectedOption) {
            return React.createElement(SelectPreview, { status: selectedOption.preview }, selectedOption.label);
        }
        return React.createElement(React.Fragment, null);
    };
    const renderSelectButton = () => {
        const ButtonComp = customButton || StyledSelectButton;
        return (React.createElement(ButtonComp, Object.assign({ "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, isDisabled: isDisabled, isOpen: visible }, selectBtnIdAttrs, { tooltip: titleTooltipText(), dropdownId: `${id}-dropdown`, onClick: getTargetProps().onClick, error: error, ref: setBtnRef, size: size, placeholder: placeholder, onKeyDown: handleSelectInputKeyDown }, (customButton && { selectedOption }), (error && { "aria-describedby": errorIdAttrs.id }), (children && { unstyled: true })), children ? children : React.createElement(React.Fragment, null, renderSelectButtonText())));
    };
    const handleSelectInputKeyDown = (e) => {
        if ((e.key === "ArrowUp" || e.key === "ArrowDown") && !visible) {
            trapEvent(e);
            setVisible(true);
        }
    };
    const titleTooltipText = useCallback(() => {
        if (tooltip) {
            return tooltip;
        }
        else if (multiSelect) {
            let multiSelectTooltip = "";
            // creating multiselect tooltip content by adding line break
            currentValues === null || currentValues === void 0 ? void 0 : currentValues.forEach((el, index) => {
                if (index === 0) {
                    const option = options.find((o) => o.value === el);
                    multiSelectTooltip = option ? option.label : "";
                }
                else if (index < MULTISELECT_MAX_VISIBLE_TOOLTIP_LABELS) {
                    const option = options.find((o) => o.value === el);
                    multiSelectTooltip = option ? `${multiSelectTooltip}\n${option.label}` : multiSelectTooltip;
                }
            });
            if (currentValues && (currentValues === null || currentValues === void 0 ? void 0 : currentValues.length) > MULTISELECT_MAX_VISIBLE_TOOLTIP_LABELS) {
                const msg = currentValues.length - MULTISELECT_MAX_VISIBLE_TOOLTIP_LABELS > 1
                    ? t("tooltipExtraPlural", {
                        ns: "select",
                        nb: currentValues.length - MULTISELECT_MAX_VISIBLE_TOOLTIP_LABELS,
                    })
                    : t("tooltipExtra", { ns: "select" });
                // Add a message to not show all labels if too many
                multiSelectTooltip = `${multiSelectTooltip}\n${msg}`;
            }
            return multiSelectTooltip;
        }
        return undefined;
    }, [tooltip, multiSelect, currentValues, options]);
    const hasErrorMessage = typeof error === "string";
    return (React.createElement(Box, Object.assign({}, idAttrs(), { width: width, display: "inline-block", onClick: trapEvent }, rest),
        React.createElement(Box, { position: "relative" },
            React.createElement("input", Object.assign({ id: `${id}-input`, type: "hidden", defaultValue: multiSelect ? selectedOptions === null || selectedOptions === void 0 ? void 0 : selectedOptions.join(",") : selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value, name: name }, (error && Object.assign({ "aria-invalid": true }, (hasErrorMessage && { "aria-errormessage": errorIdAttrs.id }))))),
            renderSelectButton(),
            error && renderError(hasErrorMessage ? error : undefined)),
        disablePortal ? dropdown : visible ? React.createElement(FloatingPortalContainer, { container: container, content: dropdown }) : null));
});
export default Select;
//# sourceMappingURL=Select.js.map